import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { playerEvents } from "../lib/api";
import { GAMES, FORMATS } from "../constants/theme";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Dices, ArrowLeft } from "lucide-react";

export default function CreatePlayerEventPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", game: "", format: "", date: "", start_time: "", city: "", location_type: "home", description: "", max_players: 4, reminder_hours: 5 });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.game) return toast.error("Choisissez un jeu");
    if (!form.date) return toast.error("Choisissez une date");
    setSaving(true);
    try {
      const res = await playerEvents.create({
        title: form.title, game: form.game,
        formats: form.format ? [form.format] : [],
        date: form.date, start_time: form.start_time,
        location: { type: form.location_type, city: form.city },
        description: form.description, max_players: Number(form.max_players),
        reminder_hours: Number(form.reminder_hours),
      });
      toast.success("Partie créée !");
      navigate(`/game/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const formats = form.game && FORMATS[form.game] ? FORMATS[form.game] : [];

  return (
    <div className="max-w-2xl mx-auto" data-testid="create-player-event-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#888] hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />Retour</button>
      <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-6 space-y-4">
        <h1 className="text-white text-xl font-bold flex items-center gap-2"><Dices className="w-5 h-5 text-[#7B68EE]" />Créer une partie</h1>
        <p className="text-[#888] text-sm">Une partie est toujours gratuite (max 16 joueurs). Les joueurs manifestent leur intérêt, vous confirmez qui participe.</p>
        <div>
          <label className="text-[#B0B0B0] text-sm mb-1 block">Titre</label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="EDH du mardi soir" className="bg-[#252525] border-[#333] text-white" data-testid="pe-title" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Jeu *</label>
            <Select value={form.game} onValueChange={(v) => { set("game", v); set("format", ""); }}>
              <SelectTrigger className="bg-[#252525] border-[#333] text-white" data-testid="pe-game"><SelectValue placeholder="Jeu" /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#333]">{GAMES.map((g) => <SelectItem key={g.id} value={g.id} className="text-white">{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Format</label>
            <Select value={form.format} onValueChange={(v) => set("format", v)} disabled={!formats.length}>
              <SelectTrigger className="bg-[#252525] border-[#333] text-white" data-testid="pe-format"><SelectValue placeholder="Format" /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#333]">{formats.map((f) => <SelectItem key={f} value={f} className="text-white">{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Date *</label>
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="bg-[#252525] border-[#333] text-white" data-testid="pe-date" />
          </div>
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Heure</label>
            <Input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="bg-[#252525] border-[#333] text-white" data-testid="pe-time" />
          </div>
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Ville / lieu</label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Lyon" className="bg-[#252525] border-[#333] text-white" data-testid="pe-city" />
          </div>
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Joueurs max (≤16)</label>
            <Input type="number" min={2} max={16} value={form.max_players} onChange={(e) => set("max_players", e.target.value)} className="bg-[#252525] border-[#333] text-white" data-testid="pe-max" />
          </div>
          <div>
            <label className="text-[#B0B0B0] text-sm mb-1 block">Rappel avant la partie</label>
            <Select value={String(form.reminder_hours)} onValueChange={(v) => set("reminder_hours", v)}>
              <SelectTrigger className="bg-[#252525] border-[#333] text-white" data-testid="pe-reminder"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#333]">
                <SelectItem value="2" className="text-white">2 heures avant</SelectItem>
                <SelectItem value="5" className="text-white">5 heures avant</SelectItem>
                <SelectItem value="24" className="text-white">24 heures avant</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-[#B0B0B0] text-sm mb-1 block">Description</label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Précisez le niveau, le lieu exact, etc." className="bg-[#252525] border-[#333] text-white" data-testid="pe-desc" />
        </div>
        <Button onClick={submit} disabled={saving} className="w-full bg-[#7B68EE] hover:bg-[#6A5ACD] h-11" data-testid="pe-submit">{saving ? "Création..." : "Créer la partie"}</Button>
      </div>
    </div>
  );
}
